import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Card,
  FormGroup,
  InputGroup,
  Spinner,
} from "@blueprintjs/core";
import { useAuth } from "../../auth/AuthContext";
import { useOrg } from "../../auth/OrgContext";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

interface Org {
  id: string;
  name: string;
  role?: string; // human-readable
  role_code?: string; // internal code
  created_at?: string;
}

export default function Organizations() {
  const { idToken, loading: authLoading } = useAuth();
  const { handleLeaveOrg: contextLeaveOrg } = useOrg();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchOrgs = useCallback(async () => {
    if (!idToken) {
      console.warn("[Organizations] No idToken, skipping org fetch");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/orgs`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      console.log("[Organizations] /api/orgs response status:", res.status);
      if (res.ok) {
        const data = await res.json();
        console.log("[Organizations] Orgs fetched:", data);
        setOrgs(data);
      } else {
        console.warn(
          "[Organizations] Failed to fetch orgs, status:",
          res.status,
        );
      }
    } catch (err) {
      console.error("[Organizations] Failed to fetch organizations:", err);
    }
    setLoading(false);
  }, [idToken]);

  useEffect(() => {
    if (idToken) fetchOrgs();
  }, [idToken, fetchOrgs]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !idToken) {
      console.warn("[Organizations] Invalid org name or missing idToken");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/orgs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ name }),
      });
      console.log("[Organizations] Create org response status:", res.status);
      if (res.ok) {
        setName("");
        await fetchOrgs(); // Refetch to get correct org/role and update context
      } else {
        console.warn(
          "[Organizations] Failed to create org, status:",
          res.status,
        );
      }
    } catch (err) {
      console.error("[Organizations] Failed to create organization:", err);
    }
    setCreating(false);
  };

  const handleLeaveOrg = async (orgId: string) => {
    if (!idToken) return;
    if (!window.confirm("Are you sure you want to leave this organization?"))
      return;
    try {
      const res = await fetch(`${API_BASE}/orgs/${orgId}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (res.ok) {
        contextLeaveOrg(orgId); // update context, which updates org switcher and currentOrg
      } else {
        alert("Failed to leave organization.");
      }
    } catch (err) {
      alert("Error leaving organization.");
    }
  };

  if (authLoading || loading) {
    return (
      <div style={{ textAlign: "center", padding: "4rem" }}>
        <Spinner intent="primary" size={40} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <form onSubmit={handleCreate} style={{ marginBottom: "2rem" }}>
        <FormGroup label="Create a new organization" labelFor="org-name">
          <InputGroup
            id="org-name"
            placeholder="Organization name"
            value={name}
            onChange={(e) => setName((e.target as HTMLInputElement).value)}
          />
        </FormGroup>
        <Button type="submit" intent="primary" loading={creating}>
          Create
        </Button>
      </form>

      {orgs.map((o) => (
        <Card key={o.id} style={{ marginBottom: "1rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{o.name}</div>
              <div style={{ fontSize: "0.95em", color: "#888" }}>
                Role: {o.role || "Member"}
              </div>
            </div>
            {o.role_code !== "org_admin" && (
              <Button
                intent="danger"
                minimal
                onClick={() => handleLeaveOrg(o.id)}
              >
                Leave
              </Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
